/**
 * i18n Context and Hook
 * Provides internationalization support for the admin interface
 */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import messages, { type Language, type I18nMessages, type FieldTranslation } from './messages';

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
  getFieldTranslation: (fieldName: string) => FieldTranslation | undefined;
  getSectionTitle: (sectionKey: string) => string;
  getSectionDescription: (sectionKey: string) => string | undefined;
  messages: I18nMessages;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

// Detect browser language
function detectLanguage(): Language {
  if (typeof navigator !== 'undefined') {
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('zh')) {
      return 'zh';
    }
  }
  return 'en';
}

// Get stored language preference
function getStoredLanguage(): Language | null {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('admin_language');
    if (stored === 'zh' || stored === 'en') {
      return stored;
    }
  }
  return null;
}

// Store language preference
function storeLanguage(lang: Language): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('admin_language', lang);
  }
}

interface I18nProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

export function I18nProvider({ children, defaultLanguage }: I18nProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    return defaultLanguage || getStoredLanguage() || detectLanguage();
  });

  const currentMessages = useMemo(() => messages[language], [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    storeLanguage(lang);
  }, []);

  // Translate a key path like "common.save" or "actions.saveChanges"
  const t = useCallback((key: string, params?: Record<string, string>): string => {
    const parts = key.split('.');
    let value: unknown = currentMessages;

    for (const part of parts) {
      if (value && typeof value === 'object' && part in value) {
        value = (value as Record<string, unknown>)[part];
      } else {
        // Return key if not found
        return key;
      }
    }

    if (typeof value !== 'string') {
      return key;
    }

    // Replace parameters like {value} with actual values
    if (params) {
      let result = value;
      for (const [paramKey, paramValue] of Object.entries(params)) {
        result = result.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), paramValue);
      }
      return result;
    }

    return value;
  }, [currentMessages]);

  // Get field translation object for a field name
  const getFieldTranslation = useCallback((fieldName: string): FieldTranslation | undefined => {
    return currentMessages.fields[fieldName];
  }, [currentMessages]);

  // Get section title
  const getSectionTitle = useCallback((sectionKey: string): string => {
    return currentMessages.sections[sectionKey] || sectionKey;
  }, [currentMessages]);

  // Get section description (optional)
  const getSectionDescription = useCallback((sectionKey: string): string | undefined => {
    return currentMessages.sectionDescriptions?.[sectionKey];
  }, [currentMessages]);

  const contextValue = useMemo(() => ({
    language,
    setLanguage,
    t,
    getFieldTranslation,
    getSectionTitle,
    getSectionDescription,
    messages: currentMessages,
  }), [language, setLanguage, t, getFieldTranslation, getSectionTitle, getSectionDescription, currentMessages]);

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

// Utility hook to get translation for a specific field
export function useFieldTranslation(fieldName: string) {
  const { getFieldTranslation, language } = useI18n();
  return useMemo(() => {
    const translation = getFieldTranslation(fieldName);
    return {
      label: translation?.label || fieldName,
      help: translation?.help,
      placeholder: translation?.placeholder,
      invalidMessage: translation?.invalidMessage,
      options: translation?.options,
      language,
    };
  }, [fieldName, getFieldTranslation, language]);
}
